import { ArrowBackIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Select } from "@chakra-ui/react";
import { currencyFormat, DEFAULT_BASIC_ITEMS, InputItemCategory } from "@kg-pos/common";
import {
  BulkUpdateItemsInput,
  ItemCategory,
  ItemDataFragment,
  ItemResponse,
  useBulkUpdateItemsMutation,
  useCreateItemMutation,
  useGetItemsByStoreIdQuery,
} from "__generated__/graphql";
import { ErrorLayout } from "components/layouts/error";
import { MainTable } from "components/molecules/main-table";
import { BulkItemForm } from "components/organisms/bulk-item-form";
import { ItemForm } from "components/organisms/item-form";
import { itemCategoryOptions } from "constant/item";
import { StoreContext } from "contexts/store.context";
import { ItemSubmit } from "interfaces";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router";
import { routeConstants } from "routes";

export const isDefaultBasicItem = (item: ItemDataFragment | undefined): boolean => {
  return (
    item?.category === ItemCategory.BASIC_CHARGE &&
    (item?.name === DEFAULT_BASIC_ITEMS.tableCharge.itemName ||
      item?.name === DEFAULT_BASIC_ITEMS.set.itemName)
  );
};

const columns = [
  {
    Header: "",
    accessor: "checkBox",
    Cell: (props: any) =>
      // セット、テーブルチャージは一括変更されたくないのでチェックボックスなし
      isDefaultBasicItem(props.row.original) ? null : (
        <input className="items-check-boxes" type="checkbox" item-id={props.row.original.id} />
      ),
  },
  {
    Header: "商品名",
    accessor: "name",
  },
  {
    Header: "カテゴリー",
    accessor: "category",
    Cell: (props: any) => (
      <div>{InputItemCategory[props.value as keyof typeof InputItemCategory]}</div>
    ),
  },
  {
    Header: "価格",
    accessor: "price",
    Cell: (props: any) => <div>{currencyFormat(props.value)}</div>,
  },
  {
    Header: "表示優先度",
    accessor: "sortNo",
  },
];

export const ItemLayout: React.VFC = () => {
  const { push } = useHistory();
  const [isForm, setIsForm] = useState<boolean>(false);
  const [isBulkForm, setIsBulkForm] = useState<boolean>(false);
  const [bulkItems, setBulkItems] = useState<ItemResponse[]>([]);
  const { storeId, firstServe } = useContext(StoreContext);
  const { loading, error, data, refetch } = useGetItemsByStoreIdQuery({
    variables: { storeId },
    fetchPolicy: "cache-and-network",
  });
  const [createItem] = useCreateItemMutation();
  const [bulkUpdateItems] = useBulkUpdateItemsMutation();
  const [category, setCategory] = useState("");

  if (loading || !data) return null;
  if (error) return <ErrorLayout />;

  const items = (data?.itemsByStoreId ?? [])
    // 特殊注文・ファーストサーブを除く
    .filter((item) => {
      if (item.category === ItemCategory.UNIQUE_ORDER) {
        return false;
      }
      if (item.category === ItemCategory.FIRST_SERVE && !firstServe) {
        return false;
      }

      return true;
    })
    // カテゴリーでフィルター
    .filter((item) => category === "" || item.category === category);

  const backToTable = async (): Promise<void> => {
    setIsForm(false);
    setIsBulkForm(false);
    await refetch();
  };

  const handleClick = (row: ItemResponse): void => {
    push(`${routeConstants.items}/${row.id}?storeId=${storeId}`);
  };

  const submit = async (input: ItemSubmit): Promise<void> => {
    await createItem({
      variables: { storeId, ...input },
    });
  };

  const bulkUpdateSubmit = async (input: BulkUpdateItemsInput) => {
    await bulkUpdateItems({
      variables: { ids: bulkItems.map((item) => item.id), ...input },
    });
  };

  const updateBulkItems = () => {
    const checkBoxes = document.querySelectorAll(
      'input.items-check-boxes[type="checkbox"]:checked'
    );
    const currentItems: ItemResponse[] = [];
    checkBoxes.forEach((checkBox) => {
      const checkedItemId = checkBox.getAttribute("item-id");
      const checkedItem = data.itemsByStoreId.find((item) => item?.id === checkedItemId);
      if (checkedItem) {
        currentItems.push(checkedItem);
      }
    });
    setBulkItems(currentItems);
  };

  return (
    <>
      {isForm ? (
        <Box>
          <ArrowBackIcon mb="6" onClick={backToTable} className="cursor-pointer" />
          <ItemForm backToTable={backToTable} submit={submit} />
        </Box>
      ) : isBulkForm ? (
        <Box>
          <ArrowBackIcon mb="6" onClick={backToTable} className="cursor-pointer" />
          <BulkItemForm items={bulkItems} backToTable={backToTable} submit={bulkUpdateSubmit} />
        </Box>
      ) : (
        <>
          <Flex flexWrap="wrap" mb={2}>
            <Box>
              <Button
                colorScheme="blue"
                mb={4}
                mr={4}
                fontSize="sm"
                onClick={() => setIsForm(true)}
              >
                商品を登録する
              </Button>
            </Box>
            <Box>
              <Button
                colorScheme="blue"
                mb={4}
                mr={4}
                fontSize="sm"
                onClick={() => {
                  updateBulkItems();
                  setIsBulkForm(true);
                }}
              >
                商品を一括編集
              </Button>
            </Box>
            {/* カテゴリー選択プルダウン */}
            <Select
              fontSize="sm"
              placeholder=""
              width="130px"
              mb={4}
              onChange={(e) => setCategory(e.target.value)}
              defaultValue={category}
            >
              <option value=""></option>
              {itemCategoryOptions
                .filter((option) => {
                  // ファーストサーブなし店舗は選択肢にFSなし
                  if (!firstServe && option.value === ItemCategory.FIRST_SERVE) {
                    return false;
                  }

                  return true;
                })
                .map((option, key) => (
                  <option key={key} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </Select>
          </Flex>
          <MainTable data={items} columns={columns} handleClick={handleClick} />
        </>
      )}
    </>
  );
};

// 複数選択チェックボックス
// https://tariqul-islam-rony.medium.com/multiple-checkbox-handling-by-react-js-84b1d49a46c6
// export const MultipleCheckBox: React.VFC<{
//   id: string;
//   name: string;
//   checked: boolean;
//   register: any;
//   textProps?: TextProps;
// }> = ({ id, name, checked, register, textProps }) => (
//   <Text as="label" display="flex" alignItems="center" {...textProps} className="custom-checkbox">
//     {/* <Text fontSize="md" fontWeight="bold" ml={2}>
//       {name}
//     </Text> */}
//     <input type="checkbox" name={id} {...register(id)} defaultChecked={checked} />
//     <span className="checkmark"></span>
//   </Text>
// );
